import React, { useContext, useEffect, useReducer } from "react"
import { useHistory } from 'react-router-dom'

import { collection, doc, addDoc, getDoc, getDocs, updateDoc, deleteDoc, deleteField, onSnapshot, query, where, serverTimestamp } from "@firebase/firestore"
import { db } from "../firebase"
import { defaultCurrency, defaultList, defaultTax, defaultRoles } from "../data/defaultData"
import { showLoader } from "../utils/loader"
import { message } from "antd"

const Store = React.createContext()

const optionsRef = "options"

export function useStore() {
  return useContext(Store)
}

const initialState = {
  options: defaultList,
  tax: defaultTax,
  currency: defaultCurrency,
  roles: defaultRoles,
  item: {}
}

function reducer(state, action) {
  switch (action.type) {
    case "options": {
      let tax = action.items.find(item => item.type === 'tax') || defaultTax
      let currency = action.items.find(item => item.type === 'currency') || defaultCurrency
      // let roles = action.items.find(item => item.type === 'roles').values || defaultRoles
      return {
        ...state,
        options: {
          items: action.items,
          status: action.status
        },
        tax: {
          name: tax.name,
          rate: tax.value
        },
        currency: {
          name: currency.name,
          iso: currency.value
        },
        // roles: roles
      }
    }
    case "item": {
      let tax = action.items.find(item => item.type === 'tax') || defaultTax
      let currency = action.items.find(item => item.type === 'currency') || defaultCurrency
      return {
        ...state,
        options: {
          items: action.items,
          status: action.status
        },
        tax: {
          name: tax.name,
          rate: tax.value
        },
        currency: {
          name: currency.name,
          iso: currency.value
        }
      }
    }
    default:
      return state
  }
}

export default function FireStoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const history = useHistory()

  useEffect(() => {
    const unSubOptions = onSnapshot(query(collection(db, optionsRef)), querySnapshot => {
      var items = []
      querySnapshot.forEach((docSnap, i) => {
        if (docSnap.exists) {
          items.push({
            ...docSnap.data(),
            id: docSnap.id
          })
        }
      })
      dispatch({
        type: "options",
        items: items,
        status: "idle"
      })
    })
    return () => {
      unSubOptions()
    }
  }, [])

  /**
   *
   * @param {Table Name} table
   * @param {*} id
   * @returns
   */
  const getDocument = (table, id) => new Promise((resolve, reject) => {
    if (!table) {
      reject("Table is required")
      return
    }
    if (!id) {
      reject("id is required")
      return
    }
    getDoc(doc(collection(db, table), id))
      .then(docSnap => resolve({ id: docSnap.id, ...docSnap.data() }))
      .catch(error => {
        reject(error)
      })
  })

  /**
 *
 * @param {Table Name} table
 * @param {*} searchBy
 * @param {*} searchValue
 * @returns
 */
  const getDocuments = (table, condition = [], between = [], loader = true) => new Promise((resolve, reject) => {
    if (!table) {
      // new Throw("Table is required")
      return
    }
    loader && showLoader(true)
    const tableRef = collection(db, table)
    // let docRef = getDocs(collection(db, table))
    let queryConstraints = []
    if (condition.length >= 2) {
      // docRef = getDocs(query(collection(db, table), where(condition[0], condition[1], condition[2])))
      queryConstraints.push(where(condition[0], condition[1], condition[2]))
    }
    if (between.length >= 2) {
      between[1] && queryConstraints.push(where(between[0], "<=", between[1]))
      between[2] && queryConstraints.push(where(between[0], ">=", between[2]))
    }

    getDocs(query(tableRef, ...queryConstraints))
      .then(querySnapshot => {
        var items = []
        querySnapshot.forEach((docSnap) => {
          if (docSnap.exists) {
            items.push({
              ...docSnap.data(),
              id: docSnap.id
            })
          }
          return resolve(items)
        })
        // .then(snapshot => {
        //   loader && showLoader(false)
        //   return snapshot
        // })

        // dispatch({
        //   type: table,
        //   items: items,
        //   status: "idle"
        // })
      })
      .catch(error => {
        console.log(error)
        return reject(error)
        // return error
      })
      .finally(() => {
        loader && showLoader(false)
      })
  })

  /**
   *
   * @param {Table Name} table
   * @param {*} searchBy
   * @param {*} searchValue
   * @param {*} stickyCondition
   * @param {*} loader
   * @returns
   */
  const searchDocuments = (table, searchBy, searchValue, stickyCondition = [], loader = true) => new Promise((resolve, reject) => {
    let queryConstraints = []
    loader && showLoader(true)
    if (stickyCondition.length >= 2) {
      queryConstraints.push(where(stickyCondition[0], stickyCondition[1], stickyCondition[2]))
    }
    queryConstraints.push(where(searchBy, ">=", searchValue))
    queryConstraints.push(where(searchBy, "<=", searchValue + "\uf8ff"))

    getDocs(query(collection(db, table), ...queryConstraints))
      .then(snapshot => {
        loader && showLoader(false)
        var items = []
        snapshot.forEach((doc, i) => {
          if (doc.exists) {
            items.push({
              ...doc.data(),
              id: doc.id
            })
          }
        })
        return resolve(items)
      })
      .catch(error => {
        console.log(error)
        return reject(error)
      })
      .finally(() => {
        loader && showLoader(false)
      })
  })

  /**
   *
   * @param {Table Name} table
   * @param {*} payload
   * @param {*} item
   * @param {*} redirectTo
   */
  async function addDocument(table, payload, item = "Item", redirectTo = "") {
    showLoader(true)
    payload.createdAt = serverTimestamp()
    addDoc(collection(db, table), payload)
      .then(docSnap => {
        message.success(`${item} Added`)
        redirectTo && history.push(redirectTo)
      })
      .catch(error => {
        var errorMessage = error.message
        console.log('error', error)
        message.error(errorMessage)
      })
      .finally(() => {
        showLoader(false)
      })
  }

  /**
   * @param {TableName} table
   * @param {*} payload
   * @param {*} id
   * @param {*} item
   * @param {*} redirectTo
   */
  async function updateDocument(table, payload, id, item = "Item", redirectTo = "") {
    if (!id) {
      message.error("id not found")
      return
    }
    if (!payload) {
      message.error("payload not found")
      return
    }
    showLoader(true)
    payload.updatedAt = serverTimestamp()
    updateDoc(doc(db, table, id), payload)
      .then(docSnap => {
        message.success(`${item} Updated`)
        redirectTo && history.push(redirectTo)
      })
      .catch(error => {
        var errorMessage = error.message
        console.log('error', error)
        message.error(errorMessage)
      })
      .finally(() => {
        showLoader(false)
      })
  }

  /**
   * @param {TableName} table
   * @param {FieldName} fieldName
   * @param {FieldValue} fieldValue
   * @param {id} id
   * @param {Item} item
   * @param {redirect to} redirectTo
   */
  const updateDocumentField = (table, field, value, id, customMessage) => new Promise((resolve, reject) => {
    if (!id) {
      message.error("id not found")
      return
    }
    if (!field) {
      message.error("field not found")
      return
    }
    if (!value) {
      message.error("value not found")
      return
    }
    showLoader(true)
    updateDoc(doc(db, table, id), field, value)
      .then(docSnap => {
        message.success(customMessage)
        resolve(true)
      })
      .catch(error => {
        console.log('error', error)
        reject(error)
      })
      .finally(() => {
        showLoader(false)
      })
  })

  /**
   *
   * @param {Table Name} table
   * @param {*} id
   * @param {*} item
   */
  const deleteDocument = (table, id, item = "Item") => new Promise((resolve) => {
    if (!id) {
      message.error("id not found")
      return
    }
    showLoader(true)
    deleteDoc(doc(db, table, id))
      .then(() => {
        message.success(`${item} Deleted`)
        return resolve(id)
      })
      .catch(error => {
        // var errorCode = error.code;
        var errorMessage = error.message
        message.error(errorMessage)
      })
      .finally(() => {
        showLoader(false)
      })
  })

  /**
   *
   * @param {Table Name} table
   * @param {*} id
   * @param {*} item
   */
  const deleteDocumentField = (table, id, fieldName, item = 'Item') => new Promise((resolve) => {
    if (!id) {
      message.error("id not found")
      return
    }
    showLoader(true)
    const docRef = doc(db, table, id)
    updateDoc(docRef,
      {
        [fieldName]: deleteField()
      })
      .then(() => {
        message.success(`${item} Deleted`)
        return resolve(id)
      })
      .catch(error => {
        // var errorCode = error.code;
        var errorMessage = error.message
        message.error(errorMessage)
      })
      .finally(() => {
        showLoader(false)
      })
  })

  console.log("FireStoreContext", state)

  return (
    <Store.Provider value={{
      ...state,
      dispatch,
      getDocument,
      getDocuments,
      searchDocuments,
      addDocument,
      updateDocument,
      updateDocumentField,
      deleteDocument,
      deleteDocumentField,
    }}>
      {children}
    </Store.Provider>
  )
}
